import { rgba } from 'polished';
import { index, Options } from 'shared/assets/styles';
import { typographyToCss } from 'shared/lib/mui';
import styled from 'styled-components';

import { rowHeight } from './constants';

export const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
`;

export const HeaderRow = styled.div`
  display: flex;
  flex-shrink: 0;
`;

export const GoalListHeader = styled.div`
  display: flex;
  align-items: center;

  width: 30rem;

  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  border-right: 1px solid ${({ theme }) => theme.palette.divider};
`;

export const GoalListTitle = styled.h2`
  ${({ theme }) => typographyToCss(theme.typography.caption)};
  padding: ${({ theme }) => `0 ${theme.spacing(5)}`};
  margin: 0;

  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const TimelineHeader = styled.div`
  flex: 1;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  background-color: ${({ theme }) => theme.palette.background.default};
  overflow-x: hidden;
`;

export const TimelineHeaderContent = styled.div<{ $timelineGridWidth: number }>`
  width: ${({ $timelineGridWidth }) => `${$timelineGridWidth}px`};
`;

export const MonthsHeaderContainer = styled.div`
  position: relative;
  height: 3.2rem;
`;

export const MonthHeader = styled.div<{
  $left: number;
  $width: number;
  $isStartOfYear: boolean;
}>`
  ${({ theme, $isStartOfYear }) =>
    typographyToCss(
      $isStartOfYear ? theme.typography.caption : theme.typography.overline,
    )};
  position: absolute;
  top: 0;
  left: ${({ $left }) => `${$left}px`};
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: ${({ $width }) => `${$width}px`};
  height: 3.2rem;
  color: ${({ theme, $isStartOfYear }) =>
    $isStartOfYear ? theme.palette.text.primary : theme.palette.grey[600]};
  border-right: 1px solid transparent;
`;

export const BodyContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
`;

export const Body = styled.div`
  min-height: 100%;
  display: flex;
`;

export const GoalListBody = styled.div`
  width: 30rem;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => theme.palette.divider};
  z-index: ${index(Options.TOPBAR)};
`;

export const CategoryList = styled.div<{ $height: number }>`
  position: relative;
  min-height: 100%;
  height: ${({ $height }) => `${$height}px`};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  box-shadow: 0.2rem 0 0.4rem 0
    ${({ theme }) => rgba(theme.palette.common.black, 0.04)};
`;

// Wraps the horizontally-scrolling TimelineBody so the edge-arrows overlay can
// pin to the viewport edges (position: relative anchor) while the body scrolls.
export const TimelineViewport = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  min-width: 0;
`;

export const TimelineBody = styled.div`
  flex-grow: 1;
  min-width: 0;
  overflow-x: auto;
`;

// Cursor-following replacement for the native drag ghost (suppressed with a
// transparent image) — a native ghost can't be hidden mid-drag once the cursor
// reaches the canvas, where the placeholder bar is the drop feedback.
export const SidebarDragPreview = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${index(Options.DND_MOVING_ITEM)};

  visibility: hidden;
  pointer-events: none;
`;

export const SidebarDragPreviewCard = styled.div`
  width: 28rem;
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  overflow: hidden;

  background: ${({ theme }) => theme.palette.background.default};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  font-family: ${({ theme }) => theme.typography.fontFamily};
`;

export const TimelineGrid = styled.div<{
  $timelineGridWidth: number;
  $height: number;
}>`
  position: relative;
  padding: ${({ theme }) => `${theme.spacing(2.5)} 0`};

  width: ${({ $timelineGridWidth }) => `${$timelineGridWidth}px`};
  min-height: 100%;
  height: ${({ $height }) => `${$height}px`};
`;

export const MonthContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  pointer-events: none;
`;

export const WeekendContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  pointer-events: none;
`;

export const WeekendBand = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => `${$left}px`};

  width: ${({ $width }) => `${$width}px`};
  background: ${({ theme }) => theme.palette.grey[50]};
  opacity: 0.7;
`;

// Vertical line at the 1st of each month (Month timeframe only) — the weekly
// columns hide their borders, so months need their own dividers.
export const MonthDividerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  pointer-events: none;
`;

export const MonthDivider = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => `${$left}px`};

  width: 1px;
  background: ${({ theme }) => theme.palette.divider};
`;

export const MonthBg = styled.div<{
  $left: number;
  $width: number;
  $hideBorder?: boolean;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => `${$left}px`};
  box-sizing: border-box;
  width: ${({ $width }) => `${$width}px`};
  border-right: 1px solid
    ${({ theme, $hideBorder }) =>
      $hideBorder ? 'transparent' : theme.palette.divider};
`;

export const GoalRow = styled.div<{ $top: number }>`
  position: absolute;
  width: 100%;
  top: ${({ $top }) => `${$top}px`};
  height: ${rowHeight}px;
`;

export const GoalRowInner = styled.div`
  position: relative;
  height: 100%;
`;
